import { Flex, Layout, Menu, Select, Tooltip } from "antd";
import type { ItemType, MenuItemType } from "antd/es/menu/interface";
import { useFlags } from "flagsmith/react";
import { useAccessLevel } from "hooks/useAccessLevel";
import { useAdvancedRecAccess } from "hooks/useAdvancedRecAccess";
import { useCompanies } from "hooks/useCompanies";
import { useFileManagerAccess } from "hooks/useFileManagerAccess";
import { usePluginAccess } from "hooks/usePluginAccess";
import { useSidebarCompanyId } from "hooks/useSidebarCompanyId";
import {
	Activity,
	BarChart3,
	Check,
	ChevronsUpDown,
	ClipboardCheck,
	FileInput,
	FileText,
	FolderPlus,
	LayoutPanelTop,
	Plus,
	RefreshCw,
	Settings,
	ShoppingBag,
	Tag as TagIcon,
} from "lucide-react";
import {
	companyProjectsLink,
	isCompanyProjectsTab,
} from "pages/project-management/companyProjectsLink";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useCompanyStore } from "store/company.store";
import { companySwitchPath } from "./companySwitchPath";
import { SIDER_MENU_INLINE_INDENT } from "./siderLayout";

const AUTOREC_HINT = "AI Mapping & Matching Capabilities";

/** Nav-row icon geometry, from the EQU-1120 spec. */
const ICON = { size: 18, strokeWidth: 1.8 } as const;

// A hint on a menu row. antd only turns `title` into a tooltip for a row on
// the rail, so a hint that has to show in the open sider comes from this
// wrapper, and the row's `title` stays blank so antd does not forward it to
// the DOM as a second, native tooltip.
function withMenuTooltip(hint: string, label: React.ReactNode) {
	return (
		<Tooltip title={hint} placement="right">
			{label}
		</Tooltip>
	);
}

/** The first letters of a company name, for the railed switcher tile. */
function initialsOf(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0]?.toUpperCase() ?? "")
		.join("");
}

export function CompanyMenu() {
	const { data, isLoading, setLastCompany } = useCompanies();
	const { accessLevel } = useAccessLevel();
	const { isPluginDisabled } = usePluginAccess();
	const { isAdvancedRecDisabled } = useAdvancedRecAccess();
	const { isFileManagerDisabled } = useFileManagerAccess();
	const dashboardFlag = useFlags(["hide_dashboard"]);
	const reclassifyFlag = useFlags(["disable_reclassify"]);
	const shopifyFlag = useFlags(["hide_shopify"]);
	const navigate = useNavigate();
	const match = useMatch("/company/:id/*");
	const location = useLocation();
	// The Sider's railed state, as antd's Menu reads it: `AppSider` passes it
	// `collapsed || narrow`, so this is the only value that covers both the
	// stored preference and the narrow breakpoint. The stored preference on its
	// own misses the breakpoint, where the cut-off company name would carry no
	// tooltip.
	const isSiderRailed = Boolean(
		useContext(Layout._InternalSiderContext).siderCollapsed,
	);
	const [companyListOpen, setCompanyListOpen] = useState(false);

	const companyId = useSidebarCompanyId();
	const setCompany = useCompanyStore((state) => state.setCompany);

	// The company the URL names, or null when we are not on a company route.
	const urlCompanyId = match?.params.id ? Number(match.params.id) : null;

	useEffect(() => {
		if (!data) return;
		// An id the URL names is CompanyPage's to judge (see below). Checked first:
		// a no-access user has an EMPTY list, and clearing here re-opened the same
		// store ping-pong on a direct link (LANYAP-WEB-19K).
		if (companyId && companyId === urlCompanyId) return;
		if (companyId && !data.length) {
			setCompany(null);
			if (match) navigate("/");
			return;
		}
		// Heal a stale persisted company id. The store (sessionStorage
		// `company-storage`) can hold a company the current user can no longer
		// access — e.g. a former default company that was later orphaned. That
		// stale id then leaks into company-scoped requests (POST
		// companies/<id>/connection/link → 401 "no access"). If the persisted id
		// isn't in the accessible list, clear it so the fallback chain
		// (lastCompany → first accessible company) picks a valid one.
		//
		// 🔑 STALE means PERSISTED, never URL-BORNE. `CompanyPage` treats the URL
		// as the source of truth and writes `/company/:id` straight back into the
		// store, so clearing an id the URL names is a write the next render
		// undoes — the two effects then overwrite each other and re-render the
		// whole tree until React throws "Maximum update depth exceeded" (Sentry
		// LANYAP-WEB-19K). An inaccessible id in the URL is already handled where
		// it can be handled properly: `CompanyPage` sends the user back to the
		// company list on a 401/403 from the company fetch.
		if (companyId && data.length && !data.some((c) => c.id === companyId)) {
			setCompany(null);
		}
	}, [data, companyId, urlCompanyId]);

	if (!companyId) return null;
	// Gate on the SELECTED company's tier (its owning team's owner), not the
	// user's max across every team: a Pro team's company must not show
	// Advanced items because the user also sits on an Advanced team.
	const companyAccessLevel =
		data?.find((c) => c.id === companyId)?.accessLevel ?? accessLevel;
	if (!companyAccessLevel) return null;

	// Keys hidden by feature flags or plan access. They sit inside sections, so
	// they are removed at every level by `pruneMenu`, not by a top-level filter.
	const hiddenKeys = new Set<string>();
	// The close tracker's flag also turns off its setup page (EQ-2487).
	if (dashboardFlag?.hide_dashboard?.enabled) {
		hiddenKeys.add("dashboard");
		hiddenKeys.add("create-close");
	}
	// `disable_services_menu` is no longer read here: Human Assist left this
	// list in EQU-1120 to become the Hire an Expert card at the bottom of the
	// sidebar, and `HireExpertCard` reads the flag itself.
	//
	// AutoRec needs the plug-in as well as the plan.
	if (isAdvancedRecDisabled || isPluginDisabled)
		hiddenKeys.add("advanced_reconcile");
	if (isFileManagerDisabled) hiddenKeys.add("files");
	// Shopify is the only e-commerce platform the module lists, so the flag that
	// suppresses the Shopify surface suppresses the module with it.
	if (shopifyFlag?.hide_shopify?.enabled) hiddenKeys.add("ecommerce");
	if (reclassifyFlag?.disable_reclassify?.enabled) hiddenKeys.add("reclassify");

	const menuItems = lockByAccessLevel(
		pruneMenu(getMenuItems(companyId), hiddenKeys),
		companyAccessLevel,
	);

	const pathParts = match?.params["*"]?.split("/") ?? [];
	const path = pathParts[0];
	// Map 'reconciles' path (including its sub-routes like mapping-rules) to the
	// 'advanced_reconcile' (AutoRec) key for menu highlighting. The company's
	// index route renders the Financial Reports page, so it highlights that row.
	const menuKey =
		path === "reconciles" ? "advanced_reconcile" : path || "analytics";
	// Off the company routes only the Projects tab this row opened, for the
	// company still selected here, highlights a row: Company Projects
	// (EQ-2488). All Projects highlights nothing there.
	const isCompanyProjects = isCompanyProjectsTab(location, companyId);
	const selectedKey = match
		? menuKey
		: isCompanyProjects
			? "company-projects"
			: undefined;
	const selected = selectedKey
		? leafKeys(menuItems).find((key) => key === selectedKey)
		: undefined;

	const companies = data?.map((company) => ({
		label: company.alias || company.name,
		value: company.id,
	}));
	// A value with no matching option makes antd render the bare id ("491")
	// as the selected company: the URL names a company this user cannot reach,
	// or the list has not loaded yet. Show the placeholder instead.
	const selectedCompanyId = companies?.some((c) => c.value === companyId)
		? companyId
		: undefined;
	const selectedCompanyName = companies?.find(
		(c) => c.value === selectedCompanyId,
	)?.label;
	// The railed sider replaces the name with a 30px initials tile, so hovering
	// it names the company (EQ-2410). The open sider shows the whole name,
	// wrapped rather than truncated, and needs no tooltip; none shows over the
	// open company list.
	//
	// 🪤 Gated on the RAILED state, not the stored `collapsed` preference. The
	// sider also rails itself below the `lg` breakpoint without touching that
	// preference, and there the name is replaced with two letters — the narrow
	// viewport is exactly where the user can least afford to guess which
	// company's books they are in (EQU-873).
	const collapsedCompanyHint =
		isSiderRailed && !companyListOpen ? selectedCompanyName : undefined;

	// Handle company switching based on current URL section
	const handleCompanyChange = (id: number) => {
		setLastCompany(id);
		// Sync the company store immediately. Company-scoped hooks (useCompany,
		// usePlaid, the QBO connect handlers, …) read the persisted store id, not
		// the route param. Switching without setting it leaves them firing against
		// the previously-selected company (e.g. POST companies/165/connection/link
		// while viewing company 260 → 401). The CompanyPage gate also syncs on
		// route change, but setting it here removes the FullLoader flash and the
		// window where children read the stale id.
		setCompany(id);
		// Check if we're currently on a company-specific URL (top section)
		if (match) {
			// Get the current path after /company/:id/
			const currentPath = match.params["*"] || "";

			// Same page under the new company, except a page naming one record
			// (a review, a rule, a reconciliation), which drops to its list.
			navigate(`/company/${id}/${companySwitchPath(currentPath)}`);
		}
		// On a company-agnostic URL (bottom section) the store update above is
		// enough — no navigation needed.
	};

	return (
		// The tinted panel is the cue that everything inside it belongs to the
		// company named at the top, and that the WORKSPACE rows below it do not
		// (EQU-1120).
		<div
			className={`sider-company-panel${
				isSiderRailed ? " sider-company-panel--railed" : ""
			}`}
		>
			<div
				className={`sider-company-switcher${
					isSiderRailed ? " sider-company-switcher--railed" : ""
				}`}
			>
				<Tooltip title={collapsedCompanyHint} placement="right">
					<Flex>
						<Select
							// LYP-1072 - Allow users to search for company in top left dropdown box
							showSearch
							filterOption={(input, option) =>
								(option?.label ?? "")
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							variant="borderless"
							value={selectedCompanyId}
							placeholder={isLoading ? "Loading companies" : "Select a company"}
							options={companies}
							loading={isLoading}
							onChange={handleCompanyChange}
							onOpenChange={setCompanyListOpen}
							suffixIcon={
								<ChevronsUpDown
									size={16}
									strokeWidth={1.8}
									aria-hidden="true"
								/>
							}
							// On the rail there is no room for a name, so the trigger shows
							// the company's initials and the popover (which opens at its own
							// width) still lists every name in full.
							labelRender={
								isSiderRailed
									? ({ label }) => (
											<span className="sider-company-initials">
												{initialsOf(String(label ?? ""))}
											</span>
										)
									: undefined
							}
							menuItemSelectedIcon={<Check size={16} strokeWidth={2} />}
							// The popover is its own width, not the trigger's: on the rail
							// the trigger is a 30px tile and would otherwise clip every
							// company name to nothing.
							popupMatchSelectWidth={false}
							classNames={{ popup: { root: "sider-company-popup" } }}
							styles={{ popup: { root: { minWidth: 248 } } }}
							popupRender={(menu) => (
								<>
									{menu}
									{/* Two existing destinations, not new ones: the company
									    list, and the Add Company wizard's own entry point on
									    it (`pages/home/AllCompanies.page.tsx` reads this
									    router state and applies its own resume and trial-limit
									    rules). */}
									<div className="sider-company-popup-footer">
										<Link to="/">View all companies</Link>
										<Link
											to="/"
											state={{ openAddCompany: true }}
											className="sider-company-popup-add"
										>
											<Plus
												size={14}
												strokeWidth={2}
												style={{ verticalAlign: -2, marginRight: 6 }}
												aria-hidden="true"
											/>
											Add company
										</Link>
									</div>
								</>
							)}
						/>
					</Flex>
				</Tooltip>
			</div>
			<Menu
				mode="inline"
				className="sider-company-menu"
				inlineIndent={SIDER_MENU_INLINE_INDENT}
				items={menuItems}
				selectedKeys={selected ? [selected] : []}
			/>
		</div>
	);
}

type CompanyMenuItem = ItemType<MenuItemType & { accesslevel?: number }>;

/**
 * The company section of the sidebar, under three section headers: Company
 * Overview, Bookkeeping Tools and Review & Reporting, then a divider and
 * Connections & Settings (EQU-1120).
 *
 * Every row keeps the route it had. What changed in EQU-1120 is the wording,
 * the grouping and the order:
 *
 * - The "Run Task Automations" dropdown is gone. Its four tools (EQ-2434) sit
 *   flat under Bookkeeping Tools, so the sidebar has no expandable parent left
 *   and every destination is one click away.
 * - Account Reconciliations, the unsupported classic reconciler, is no longer
 *   listed. Its route still exists for anyone holding a link; the upgrade
 *   modal that fronted it went with the row.
 * - Human Assist is now the Hire an Expert card pinned to the bottom of the
 *   sidebar (`HireExpertCard`), not a row in this list.
 * - Connections & Settings (Link Platforms until EQU-1120, Platform
 *   Integrations until EQU-875, Settings & Integrations until EQ-2471) moves
 *   below a divider at the foot of the panel: it is setup, not daily work.
 * - Accounting Health (EQ-2507) is the first Company Overview row.
 * - Close Setup (Create Close Project until EQU-1167, Create Close Process
 *   until EQU-1120) keeps its `create-close` route. The page it opens still
 *   has its own heading; only this row was renamed.
 *
 * A section is an antd `type: "group"` item. It adds a header, not an indent
 * level, and cannot be opened or closed. The headers, the divider and the row
 * chrome are styled in styles/index.css.
 *
 * `accesslevel` is the plan tier a row needs. Sections carry none; their rows
 * are locked one by one (see `lockByAccessLevel`).
 */
const getMenuItems = (companyId: number): CompanyMenuItem[] => [
	{
		type: "group",
		key: "section-company-overview",
		label: "Company Overview",
		children: [
			{
				label: (
					<Link to={`/company/${companyId}/health`}>Accounting Health</Link>
				),
				key: "health",
				icon: <Activity {...ICON} />,
				accesslevel: 0,
				title: "Accounting Health",
			},
			{
				label: (
					<Link to={`/company/${companyId}/create-close`}>Close Setup</Link>
				),
				key: "create-close",
				icon: <FolderPlus {...ICON} />,
				accesslevel: 0,
				title: "Close Setup",
			},
			{
				label: (
					<Link {...companyProjectsLink(companyId)}>Company Projects</Link>
				),
				key: "company-projects",
				icon: <LayoutPanelTop {...ICON} />,
				accesslevel: 0,
				title: "Company Projects",
			},
		],
	},
	{
		type: "group",
		key: "section-bookkeeping-tools",
		label: "Bookkeeping Tools",
		children: [
			{
				label: <Link to={`/company/${companyId}/files`}>File Converter</Link>,
				key: "files",
				icon: <FileInput {...ICON} />,
				accesslevel: 0,
				title: "File Converter",
			},
			{
				label: <Link to={`/company/${companyId}/ecommerce`}>E-Commerce</Link>,
				key: "ecommerce",
				icon: <ShoppingBag {...ICON} />,
				accesslevel: 0,
				title: "E-Commerce",
			},
			{
				label: withMenuTooltip(
					AUTOREC_HINT,
					<Link to={`/company/${companyId}/reconciles`}>AutoRec</Link>,
				),
				key: "advanced_reconcile",
				icon: <RefreshCw {...ICON} />,
				accesslevel: 0,
				title: "",
			},
			{
				label: <Link to={`/company/${companyId}/reclassify`}>Reclassify</Link>,
				key: "reclassify",
				icon: <TagIcon {...ICON} />,
				accesslevel: 0,
				title: "Reclassify Transactions",
			},
		],
	},
	{
		type: "group",
		key: "section-review-reporting",
		label: "Review & Reporting",
		children: [
			{
				label: (
					<Link to={`/company/${companyId}/review`}>Financial Review</Link>
				),
				key: "review",
				icon: <ClipboardCheck {...ICON} />,
				accesslevel: 2,
				title: "Financial Review",
			},
			{
				label: (
					<Link to={`/company/${companyId}/analytics`}>Financial Reports</Link>
				),
				key: "analytics",
				icon: <FileText {...ICON} />,
				accesslevel: 0,
				title: "Financial Reports",
			},
			{
				label: <Link to={`/company/${companyId}/dashboard`}>Analytics</Link>,
				key: "dashboard",
				icon: <BarChart3 {...ICON} />,
				accesslevel: 0,
				title: "Analytics",
			},
		],
	},
	{ type: "divider", key: "section-settings-divider" },
	{
		label: (
			<Link to={`/company/${companyId}/settings`}>Connections & Settings</Link>
		),
		key: "settings",
		icon: <Settings {...ICON} />,
		accesslevel: 0,
		title: "Connections & Settings",
	},
];

function childrenOf(item: CompanyMenuItem): CompanyMenuItem[] | undefined {
	return item && "children" in item
		? (item.children as CompanyMenuItem[] | undefined)
		: undefined;
}

/**
 * Drop every row whose key is hidden, at any depth, then any section left with
 * no rows. An empty section would render as a header over nothing.
 */
function pruneMenu(
	items: CompanyMenuItem[],
	hiddenKeys: ReadonlySet<string>,
): CompanyMenuItem[] {
	return items.flatMap((item) => {
		if (!item || hiddenKeys.has(String(item.key))) return [];
		const children = childrenOf(item);
		if (!children) return [item];
		const kept = pruneMenu(children, hiddenKeys);
		return kept.length ? [{ ...item, children: kept } as CompanyMenuItem] : [];
	});
}

/**
 * Disable every row above the company's plan tier and say why on hover.
 * Sections have no tier of their own.
 */
function lockByAccessLevel(
	items: CompanyMenuItem[],
	companyAccessLevel: number,
): CompanyMenuItem[] {
	return items.map((item) => {
		const children = childrenOf(item);
		if (children) {
			return {
				...item,
				children: lockByAccessLevel(children, companyAccessLevel),
			} as CompanyMenuItem;
		}
		const required =
			item && "accesslevel" in item ? (item.accesslevel ?? 0) : 0;
		if (!item || companyAccessLevel >= required) return item;
		return {
			...item,
			disabled: true,
			label: (
				<Tooltip title={getMsgByAccessLevel(required)}>
					{"label" in item ? item.label : null}
				</Tooltip>
			),
		} as CompanyMenuItem;
	});
}

/** The keys of the rows a page can select, sections and dividers excluded. */
function leafKeys(items: CompanyMenuItem[]): string[] {
	return items.flatMap((item) => {
		const children = childrenOf(item);
		if (children) return leafKeys(children);
		if (!item || ("type" in item && item.type === "divider")) return [];
		return [String(item.key)];
	});
}

function getMsgByAccessLevel(level: number) {
	switch (level) {
		case 2:
			return "Upgrade to Advanced Tier";
		case 1:
			return "Upgrade to Pro Tier";
		default:
			return "";
	}
}
